import { memo, type CSSProperties, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import type { LabelRow, Pin, SensorStatus } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import type { SensorDto } from '../../api';
import { pinSensorIds, pinTitle } from '../bindings';
import { PIN_DOT_RADIUS } from '../stage/geometry';
import { useSensorMetaMap, useSensorValues, type SensorReading } from '../live/LiveProvider';
import { STATUS_RING, STATUS_STYLE, worstStatus } from '../live/status';
import { formatBinding, formatManual, useLocale } from '../live/values';
import { dp } from '../units';
import '../engine.css';

export type PinDotVariant = 'viewer' | 'editor';
export type LabelDensity = 'comfortable' | 'compact';

/** Device status of a pin = worst status over its primary + row sensors; `unknown` without bindings. */
export function pinStatus(pin: Pin, readings: ReadonlyMap<string, SensorReading>): SensorStatus {
  const ids = pinSensorIds(pin);
  if (!ids.length) return 'unknown';
  return worstStatus(ids.map((id) => readings.get(id)?.status ?? 'unknown'));
}

/** "Sath = 854.6 m" (name empty → value only; manual rows as typed). */
export function labelRowText(row: LabelRow, readings: ReadonlyMap<string, SensorReading>, meta: ReadonlyMap<string, SensorDto>, locale: string): string {
  const value =
    row.source.kind === 'manual'
      ? formatManual(row.source.value).text
      : formatBinding(row.source.binding, readings.get(row.source.binding.sensorId), meta.get(row.source.binding.sensorId), locale).text;
  return row.name.trim() ? `${row.name} = ${value}` : value;
}

/* ───────────────────────── dot ───────────────────────── */

export function PinDot({ status, variant = 'viewer', selected = false, className }: { status: SensorStatus; variant?: PinDotVariant; selected?: boolean; className?: string }) {
  const style = STATUS_STYLE[status];
  const alert = status === 'warning' || status === 'danger';
  if (variant === 'editor') {
    // 429:17364: 14 px dot + 6 px ring (30 %); selected (277:4915): ring 4 px at 40 %
    return (
      <span
        aria-hidden
        className={cn('absolute top-0 left-0 size-14 -translate-1/2 rounded-full', className)}
        style={{ backgroundColor: style.dot, boxShadow: `0 0 0 ${dp(selected ? 4 : 6)} ${selected ? `${style.dot}66` : STATUS_RING[status]}` }}
      />
    );
  }
  // 590:16559: 23 px halo (10 %) with a 9 px dot
  return (
    <span aria-hidden className={cn('absolute top-0 left-0 flex size-23 -translate-1/2 items-center justify-center rounded-full', className)} style={{ backgroundColor: style.halo }}>
      {alert && <span className="atnt-pin-pulse absolute inset-0 rounded-full" style={{ backgroundColor: `${style.dot}55` }} />}
      <span className="relative size-9 rounded-full" style={{ backgroundColor: style.dot, boxShadow: selected ? `0 0 0 ${dp(3)} #35b5ec` : undefined }} />
    </span>
  );
}

/* ───────────────────────── label card ───────────────────────── */

function labelPlacement(pin: Pin): CSSProperties {
  const { side, distance, offset } = pin.label;
  const gap = dp(PIN_DOT_RADIUS + distance);
  switch (side) {
    case 'right':
      return { left: gap, top: dp(offset), transform: 'translateY(-50%)' };
    case 'left':
      return { right: gap, top: dp(offset), transform: 'translateY(-50%)' };
    case 'top':
      return { bottom: gap, left: dp(offset), transform: 'translateX(-50%)' };
    case 'bottom':
      return { top: gap, left: dp(offset), transform: 'translateX(-50%)' };
  }
}

export interface PinLabelProps {
  title: string;
  rows: string[];
  density: LabelDensity;
  selected?: boolean;
  style?: CSSProperties;
  className?: string;
}

/**
 * Label card: `comfortable` (429:17365 / 276:4623 / 277:4752 — glass r10, padding 8/13, gap 6, title B 13 white, rows
 * B 13 White/70; the older 372:5710 drew rows R 14/20) or `compact` (590:16565 — r4, B 7 / B 12 / B 10).
 */
export function PinLabel({ title, rows, density, selected, style, className }: PinLabelProps) {
  if (!title && !rows.length) return null;
  if (density === 'compact') {
    return (
      <span
        className={cn('glass-label absolute flex flex-col gap-3 rounded-4 px-6 py-4 whitespace-nowrap text-white', className)}
        style={{ ...style, outline: selected ? `${dp(1.5)} solid #35b5ec` : undefined }}
      >
        {title && <span className="text-[0.4375rem] leading-[1.15] font-bold">{title}</span>}
        {rows.length > 0 && (
          <span className="flex flex-col gap-3">
            {rows.map((r, i) => (
              <span key={i} className={cn('leading-[1.2] font-bold tracking-[-0.03125rem]', i === 0 ? 'text-xs' : 'text-10')}>
                {r}
              </span>
            ))}
          </span>
        )}
      </span>
    );
  }
  return (
    <span
      className={cn('glass-label absolute flex flex-col gap-6 rounded-10 px-13 py-8 whitespace-nowrap', className)}
      style={{ ...style, outline: selected ? `${dp(1.5)} solid #35b5ec` : undefined }}
    >
      {title && <span className="text-13 leading-auto font-bold text-white">{title}</span>}
      {rows.map((r, i) => (
        <span key={i} className="text-13 leading-auto font-bold text-white-700">
          {r}
        </span>
      ))}
    </span>
  );
}

/* ───────────────────────── pin (dot + label, live) ───────────────────────── */

export interface PinMarkerProps {
  pin: Pin;
  density: LabelDensity;
  variant: PinDotVariant;
  selected?: boolean;
  dimmed?: boolean;
  onClick?: (pin: Pin, e: MouseEvent) => void;
  onPointerDown?: (pin: Pin, e: PointerEvent) => void;
  /** accessible name of an interactive pin without a title or rows (a pin just placed, no device yet) */
  fallbackLabel?: string;
  children?: ReactNode;
}

/**
 * A pin at its normalised position inside the drawing box; reads its sensors live. Memoised: with structurally
 * shared documents only the pin that changed re-renders (dragging one of 1000 pins).
 */
export const PinMarker = memo(function PinMarker({ pin, density, variant, selected, dimmed, onClick, onPointerDown, fallbackLabel, children }: PinMarkerProps) {
  const readings = useSensorValues(pinSensorIds(pin));
  const meta = useSensorMetaMap();
  const locale = useLocale();
  const status = pinStatus(pin, readings);
  const title = pinTitle(pin);
  const rows = pin.rows.map((row) => labelRowText(row, readings, meta, locale));
  const interactive = Boolean(onClick || onPointerDown);
  const body = (
    <>
      <PinDot status={status} variant={variant} selected={selected} />
      <PinLabel
        title={title}
        rows={rows}
        density={density}
        selected={selected}
        style={labelPlacement(pin)}
        className={cn(interactive && 'transition-[filter] group-hover:brightness-125 group-focus-visible:outline-2 group-focus-visible:outline-blue-1000')}
      />
    </>
  );

  return (
    <div
      data-pin-id={pin.id}
      data-status={status}
      className={cn('absolute size-0', dimmed && 'opacity-40', selected && 'z-10')}
      style={{ left: `${pin.at.u * 100}%`, top: `${pin.at.v * 100}%` }}
    >
      {interactive ? (
        <button
          type="button"
          aria-label={[title, ...rows].filter(Boolean).join(', ') || fallbackLabel || undefined}
          onClick={(e) => onClick?.(pin, e)}
          onPointerDown={(e) => onPointerDown?.(pin, e)}
          className="group absolute top-0 left-0 block size-0 cursor-pointer text-left outline-none"
        >
          {body}
        </button>
      ) : (
        body
      )}
      {children}
    </div>
  );
});
